<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 py-4 px-3">
			<div class="w-full flex justify-between items-center flex-wrap gap-2">
				<div>
					<h1 class="text-2xl font-semibold text-gray-900 m-0">Hyperlinks</h1>
					<p class="text-sm text-gray-500 mt-1 mb-0">Manage hyperlinks for different regions and languages</p>
				</div>
				<button
					type="button"
					class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6"
					@click="openCreateModal"
				>
					<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
					</svg>
					Create New
				</button>
			</div>
		</header>

		<!-- Main Content -->
		<main class="w-full p-3">
			<!-- Card -->
			<div class="bg-white rounded-xl shadow-sm overflow-hidden">
				<!-- Card Header -->
				<div class="py-4 px-6 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
					<div class="flex items-center gap-3 flex-wrap">
						<h2 class="text-lg font-semibold text-gray-900 m-0">All Hyperlinks</h2>
						<span class="inline-flex items-center py-1 px-2.5 text-xs font-medium bg-orange-2 text-orange-7 rounded-full">
							{{ totalRows }} total
						</span>
					</div>
					<div class="flex items-center gap-3 flex-wrap">
						<!-- Language Filter -->
						<select
							v-model="selectedLanguageId"
							class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-32"
							@change="currentPage = 1"
						>
							<option :value="null">All Languages</option>
							<option v-for="lang in languages" :key="lang.id" :value="lang.id">
								{{ lang.name }}
							</option>
						</select>
						<!-- Region Filter -->
						<select
							v-model="selectedRegionId"
							class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-32"
							@change="currentPage = 1"
						>
							<option :value="null">All Regions</option>
							<option v-for="region in regions" :key="region.id" :value="region.id">
								{{ region.name }}
							</option>
						</select>
						<!-- Search -->
						<div class="relative">
							<svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
							<input
								v-model="searchQuery"
								type="text"
								placeholder="Search hyperlinks..."
								class="py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm w-56 outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
							>
						</div>
						<!-- Refresh Button -->
						<button
							type="button"
							class="inline-flex items-center gap-2 py-2 px-4 border border-gray-300 bg-white rounded-lg text-sm font-medium text-gray-700 cursor-pointer transition-colors hover:bg-gray-50"
							@click="refreshData"
						>
							<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
							</svg>
							Refresh
						</button>
					</div>
				</div>

				<!-- Table -->
				<div class="overflow-x-auto">
					<!-- Loading State -->
					<div v-if="isTableLoading" class="flex justify-center items-center py-16 px-4">
						<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
						<span class="ml-3 text-gray-500 text-sm">Loading hyperlinks...</span>
					</div>

					<!-- Empty State -->
					<div v-else-if="filteredData.length === 0" class="text-center py-16 px-4">
						<svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
							<path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
						</svg>
						<h3 class="text-base font-medium text-gray-700 m-0 mb-1">No hyperlinks found</h3>
						<p class="text-sm text-gray-500 m-0">Try adjusting your search or filter.</p>
					</div>

					<!-- Data Table -->
					<table v-else class="w-full border-collapse">
						<thead>
							<tr class="bg-gray-50">
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Language</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Region</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tag</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">URL</th>
								<th class="py-3 px-6 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Delegate</th>
								<th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="item in paginatedData"
								:key="item.id"
								class="border-b border-gray-200 transition-colors hover:bg-gray-50"
							>
								<td class="py-3.5 px-6 text-sm text-gray-500 font-mono">{{ item.id }}</td>
								<td class="py-3.5 px-6 text-sm text-gray-900">{{ getLanguageName(item.languageid) }}</td>
								<td class="py-3.5 px-6 text-sm text-gray-900">{{ getRegionName(item.regionid) }}</td>
								<td class="py-3.5 px-6 text-sm text-gray-500">{{ item.tag || '-' }}</td>
								<td class="py-3.5 px-6 text-sm text-gray-500 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap" :title="item.url">{{ item.url || '-' }}</td>
								<td class="py-3.5 px-6 text-center">
									<span
										:class="[
											'inline-flex items-center py-1 px-2 text-xs font-medium rounded-full',
											item.delegate ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
										]"
									>
										{{ item.delegate ? 'Yes' : 'No' }}
									</span>
								</td>
								<td class="py-3.5 px-6 text-right">
									<div class="flex justify-end gap-2">
										<button
											type="button"
											class="inline-flex items-center py-1.5 px-3 rounded-md text-xs font-medium text-orange-7 bg-transparent border-none cursor-pointer transition-colors hover:bg-orange-2"
											@click="editHyperlink(item)"
										>
											<svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
											</svg>
											Edit
										</button>
										<button
											type="button"
											class="inline-flex items-center py-1.5 px-3 rounded-md text-xs font-medium text-red-600 bg-transparent border-none cursor-pointer transition-colors hover:bg-red-50"
											@click="confirmDelete(item)"
										>
											<svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
											</svg>
											Delete
										</button>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Pagination -->
				<div v-if="totalPages > 1" class="py-4 px-6 border-t border-gray-200 flex justify-between items-center flex-wrap gap-4">
					<p class="text-sm text-gray-500 m-0">
						Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredData.length) }} of {{ filteredData.length }} results
					</p>
					<div class="flex gap-1">
						<button
							type="button"
							:disabled="currentPage === 1"
							class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
							@click="currentPage > 1 && currentPage--"
						>
							Previous
						</button>
						<button
							v-for="page in visiblePages"
							:key="page"
							type="button"
							:class="[
								'py-2 px-3 border rounded-md text-sm cursor-pointer transition-colors',
								page === currentPage
									? 'bg-orange-7 text-white border-orange-7'
									: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
							]"
							@click="currentPage = page"
						>
							{{ page }}
						</button>
						<button
							type="button"
							:disabled="currentPage === totalPages"
							class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
							@click="currentPage < totalPages && currentPage++"
						>
							Next
						</button>
					</div>
				</div>
			</div>
		</main>

		<!-- Create Modal -->
		<div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center">
			<div class="absolute inset-0 bg-black/50" @click="closeModal"></div>
			<div class="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto m-4">
				<!-- Modal Header -->
				<div class="py-5 px-6 border-b border-gray-200 flex justify-between items-center">
					<h3 class="text-lg font-semibold text-gray-900 m-0">Create New Hyperlink</h3>
					<button
						type="button"
						class="text-gray-500 bg-transparent border-none cursor-pointer p-1 hover:text-gray-700"
						@click="closeModal"
					>
						<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>
				<!-- Modal Body -->
				<div class="p-6">
					<div class="space-y-4">
						<!-- Language -->
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1.5">Language *</label>
							<select
								v-model="newHyperlink.languageid"
								class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
							>
								<option value="">Select a language</option>
								<option v-for="lang in languages" :key="lang.id" :value="lang.id">{{ lang.name }}</option>
							</select>
						</div>
						<!-- Region -->
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1.5">Region *</label>
							<select
								v-model="newHyperlink.regionid"
								class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
							>
								<option :value="null">Select a region</option>
								<option v-for="region in regions" :key="region.id" :value="region.id">{{ region.name }}</option>
							</select>
						</div>
						<!-- PDP -->
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1.5">PDP</label>
							<select
								v-model="newHyperlink.pdpid"
								class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
							>
								<option :value="null">None</option>
								<option v-for="p in pdps" :key="p.id" :value="p.id">{{ p.name }}</option>
							</select>
						</div>
						<!-- Product Category -->
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1.5">Product Category</label>
							<select
								v-model="newHyperlink.productcategoryid"
								class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
							>
								<option :value="null">None</option>
								<option v-for="cat in productCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
							</select>
						</div>
						<!-- Tag -->
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1.5">Tag</label>
							<input
								v-model="newHyperlink.tag"
								type="text"
								placeholder="Enter tag"
								class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
							>
						</div>
						<!-- URL -->
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1.5">URL</label>
							<input
								v-model="newHyperlink.url"
								type="text"
								placeholder="https://..."
								class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
							>
						</div>
						<!-- Delegate -->
						<div class="flex items-center gap-2">
							<button
								type="button"
								:class="[
									'relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors',
									newHyperlink.delegate ? 'bg-orange-7' : 'bg-gray-300'
								]"
								@click="newHyperlink.delegate = !newHyperlink.delegate"
							>
								<span
									:class="[
										'absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow',
										newHyperlink.delegate ? 'left-[1.375rem]' : 'left-0.5'
									]"
								></span>
							</button>
							<label class="text-sm font-medium text-gray-700">Delegate</label>
						</div>
					</div>
				</div>
				<!-- Modal Footer -->
				<div class="py-4 px-6 border-t border-gray-200 flex justify-end gap-3">
					<button
						type="button"
						class="py-2 px-4 border border-gray-300 bg-white rounded-lg text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50"
						@click="closeModal"
					>
						Cancel
					</button>
					<button
						type="button"
						:disabled="isSaving || !newHyperlink.languageid || !newHyperlink.regionid"
						class="py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
						@click="createHyperlink"
					>
						{{ isSaving ? 'Creating...' : 'Create' }}
					</button>
				</div>
			</div>
		</div>

		<!-- Toast Notification -->
		<div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
			<div
				:class="[
					'flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg',
					toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'
				]"
			>
				<svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
					<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
				</svg>
				<svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
					<path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>
				<span class="text-sm font-medium text-white">{{ toastMessage }}</span>
				<button
					type="button"
					class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer hover:opacity-100"
					@click="hideToast"
				>
					<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import Swal from 'sweetalert2';
import HyperlinkService from 'services/HyperlinkService';
import LanguageService from 'services/LanguageService';
import RegionService from 'services/RegionService';
import PDPService from 'services/PDPService';
import ProductCategoryService from 'services/ProductCategoryService';

export default defineComponent({
	name: 'HyperlinksPage',
	setup() {
		const router = useRouter();
		const auth0 = useAuth0();

		const hyperlinks = ref([]);
		const languages = ref([]);
		const regions = ref([]);
		const pdps = ref([]);
		const productCategories = ref([]);
		const selectedLanguageId = ref(null);
		const selectedRegionId = ref(null);
		const isTableLoading = ref(true);
		const searchQuery = ref('');
		const currentPage = ref(1);
		const pageSize = 25;
		const showModal = ref(false);
		const isSaving = ref(false);

		const newHyperlink = ref({
			languageid: '',
			regionid: null,
			pdpid: null,
			productcategoryid: null,
			tag: '',
			url: '',
			delegate: false,
		});

		const toastMessage = ref('');
		const toastVariant = ref('success');
		let toastTimeout;

		const loggedIn = computed(() => auth0.isAuthenticated.value);

		const filteredData = computed(() => {
			let data = hyperlinks.value;
			if (selectedLanguageId.value) {
				data = data.filter(item => item.languageid === selectedLanguageId.value);
			}
			if (selectedRegionId.value) {
				data = data.filter(item => item.regionid === selectedRegionId.value);
			}
			if (searchQuery.value) {
				const query = searchQuery.value.toLowerCase();
				data = data.filter(item =>
					(item.tag && item.tag.toLowerCase().includes(query)) ||
					(item.url && item.url.toLowerCase().includes(query)) ||
					(item.id && String(item.id).includes(query))
				);
			}
			return data;
		});

		const totalRows = computed(() => filteredData.value.length);
		const totalPages = computed(() => Math.ceil(filteredData.value.length / pageSize));

		const paginatedData = computed(() => {
			const start = (currentPage.value - 1) * pageSize;
			return filteredData.value.slice(start, start + pageSize);
		});

		const visiblePages = computed(() => {
			const pages = [];
			const maxVisible = 5;
			let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
			let end = Math.min(totalPages.value, start + maxVisible - 1);
			if (end - start + 1 < maxVisible) {
				start = Math.max(1, end - maxVisible + 1);
			}
			for (let i = start; i <= end; i++) {
				pages.push(i);
			}
			return pages;
		});

		const showToast = (message, variant = 'success') => {
			toastMessage.value = message;
			toastVariant.value = variant;
			if (toastTimeout) clearTimeout(toastTimeout);
			toastTimeout = setTimeout(() => {
				toastMessage.value = '';
			}, 4000);
		};

		const hideToast = () => {
			toastMessage.value = '';
			if (toastTimeout) clearTimeout(toastTimeout);
		};

		const getLanguageName = (id) => {
			const lang = languages.value.find(l => l.id === id);
			return lang ? lang.name : id || '-';
		};

		const getRegionName = (id) => {
			const region = regions.value.find(r => r.id === id);
			return region ? region.name : '-';
		};

		const loadData = async () => {
			if (!loggedIn.value) return;

			isTableLoading.value = true;
			try {
				const params = new URLSearchParams({ limit: '0' });
				const [hyperlinkData, languageData, regionData, pdpData, categoryData] = await Promise.all([
					HyperlinkService.getAll(params),
					LanguageService.getAll(params),
					RegionService.getAll(params),
					PDPService.getAll(params),
					ProductCategoryService.getAll(params),
				]);
				hyperlinks.value = hyperlinkData;
				languages.value = languageData;
				regions.value = regionData;
				pdps.value = pdpData;
				productCategories.value = categoryData;
				currentPage.value = 1;
			} catch (error) {
				showToast(error.message || 'Failed to load hyperlinks', 'error');
			} finally {
				isTableLoading.value = false;
			}
		};

		const refreshData = () => {
			loadData();
		};

		const editHyperlink = (item) => {
			router.push(`/hyperlink/${item.id}`);
		};

		const confirmDelete = (item) => {
			Swal.fire({
				title: 'Delete Hyperlink?',
				text: 'This action cannot be undone.',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#dc2626',
				cancelButtonColor: '#6b7280',
				confirmButtonText: 'Yes, delete it',
				cancelButtonText: 'Cancel',
			}).then(async (result) => {
				if (result.isConfirmed) {
					try {
						await HyperlinkService.delete(`/api/hyperlink/${item.id}`);
						hyperlinks.value = hyperlinks.value.filter(h => h.id !== item.id);
						showToast('Hyperlink deleted successfully', 'success');
					} catch (error) {
						showToast(error.message || 'Failed to delete hyperlink', 'error');
					}
				}
			});
		};

		const openCreateModal = () => {
			newHyperlink.value = {
				languageid: '',
				regionid: null,
				pdpid: null,
				productcategoryid: null,
				tag: '',
				url: '',
				delegate: false,
			};
			showModal.value = true;
		};

		const closeModal = () => {
			showModal.value = false;
		};

		const createHyperlink = async () => {
			if (!newHyperlink.value.languageid || !newHyperlink.value.regionid) {
				showToast('Please fill in required fields', 'error');
				return;
			}

			isSaving.value = true;
			try {
				const response = await HyperlinkService.create({ ...newHyperlink.value });
				hyperlinks.value.push(response);
				showToast('Hyperlink created successfully', 'success');
				closeModal();
				router.push(`/hyperlink/${response.id}`);
			} catch (error) {
				showToast(error.message || 'Failed to create hyperlink', 'error');
			} finally {
				isSaving.value = false;
			}
		};

		watch(loggedIn, async (isLoggedIn) => {
			if (isLoggedIn) {
				loadData();
			}
		}, { immediate: true });

		watch(searchQuery, () => {
			currentPage.value = 1;
		});

		return {
			hyperlinks,
			languages,
			regions,
			pdps,
			productCategories,
			selectedLanguageId,
			selectedRegionId,
			isTableLoading,
			searchQuery,
			currentPage,
			pageSize,
			showModal,
			isSaving,
			newHyperlink,
			toastMessage,
			toastVariant,
			filteredData,
			totalRows,
			totalPages,
			paginatedData,
			visiblePages,
			getLanguageName,
			getRegionName,
			refreshData,
			editHyperlink,
			confirmDelete,
			openCreateModal,
			closeModal,
			createHyperlink,
			hideToast,
		};
	},
});
</script>
